"use client";

function PlayIcon({
  className = ""
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>;
}
function ArrowUpRightIcon({
  className = ""
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>;
}
const missionLogs = [{
  id: "001",
  videoId: "HDQBkSOmGg4",
  title: "AstroMoto Vlog #1: First Light at Fintry",
  date: "SEP 24, 2025",
  location: "Fintry Provincial Park, BC",
  tag: "MILKY WAY · TIMELAPSE",
  description: "The first solo run, and the night the AstroMoto project began. The DRZ400S loaded with camp gear, a ride out to Fintry, and an overnight Milky Way timelapse captured from under the trees — a proof of concept before any telescope was involved."
}, {
  id: "002",
  videoId: "kERAHslzcqg",
  title: "AstroMoto Vlog #2: Loading a Newtonian on a Motorcycle",
  date: "OCT 3, 2025",
  location: "Vernon, BC",
  tag: "FIELD TEST",
  description: "Before any of this could work, the rig had to survive the ride. A field test of strapping a Sky-Watcher 150PDS, AM5 mount, and tripod to the bike — and finding out whether a six-inch Newtonian and its collimation can take the road."
}, {
  id: "003",
  videoId: "AU0IGOTJGeU",
  title: "AstroMoto Vlog #3: Deep-Sky Under a Supermoon",
  date: "OCT 10, 2025",
  location: "Ellison Provincial Park, BC",
  tag: "DEEP-SKY · NARROWBAND",
  description: "A fully loaded deep-sky rig hauled to Ellison Provincial Park on the worst possible night — the October supermoon. An attempt at the Veil Nebula in narrowband, cutting through the moonlight with an L-Extreme filter and three-minute subs over Okanagan Lake."
}, {
  id: "004",
  videoId: "dS9HlE7VHlc",
  title: "AstroMoto Vlog #4: 1,100 km for Comet Lemmon",
  date: "OCT 19, 2025",
  location: "Jasper National Park, AB",
  tag: "COMET · WIDEFIELD",
  description: "1,100 km to one of Canada's darkest skies for Comet C/2025 A6 (Lemmon) — undone by a guide scope left at home. The unguided Milky Way over Jasper that came back anyway, and the discipline lesson that came with it."
}, {
  id: "005",
  videoId: "j66ThqBM-vw",
  title: "AstroMoto Vlog #5: Comet Lemmon, Captured",
  date: "OCT 24, 2025",
  location: "Vernon, BC",
  tag: "COMET · CINEMATIC",
  description: "Comet Lemmon at its brightest, shot clean from home with no long drive and nothing forgotten. A short cinematic record of the green comet and the Milky Way behind it — the payoff after the trip that went wrong."
}];
function LogEntry({
  log,
  index
}: {
  log: (typeof missionLogs)[number];
  index: number;
}) {
  const watchUrl = `https://www.youtube.com/watch?v=${log.videoId}`;
  return <article data-index={index} className="group relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-12 lg:py-16 border-b border-border first:border-t">
      {/* top hover accent bar */}
      <div className="absolute top-0 left-0 w-0 h-px bg-secondary group-hover:w-full transition-all duration-700 ease-out" />

      {/* Thumbnail */}
      <a href={watchUrl} target="_blank" rel="noopener noreferrer" aria-label={`Watch ${log.title} on YouTube`} className="lg:col-span-7 relative block aspect-video overflow-hidden bg-card border border-border/40 rounded-md">
        <img src={`https://img.youtube.com/vi/${log.videoId}/maxresdefault.jpg`} onError={e => {
        (e.currentTarget as HTMLImageElement).src = `https://img.youtube.com/vi/${log.videoId}/hqdefault.jpg`;
      }} alt={log.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-500 lg:grayscale lg:group-hover:grayscale-0 group-hover:scale-[1.02]" />
        {/* darken + play */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex items-center justify-center size-16 lg:size-20 rounded-full border border-white/40 bg-black/30 backdrop-blur-sm transition-all duration-300 group-hover:bg-black/50 group-hover:border-secondary group-hover:scale-110">
            <PlayIcon className="size-6 lg:size-7 text-white translate-x-0.5" />
          </div>
        </div>
        {/* corner log id */}
        <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 bg-black/40 backdrop-blur-sm px-2 py-1 rounded">
          LOG {log.id}
        </span>
      </a>

      {/* Content */}
      <div className="lg:col-span-5 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-mono text-5xl lg:text-6xl font-light leading-none text-border select-none">
            {log.id}
          </span>
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">
              {log.tag}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              {log.date} · {log.location}
            </span>
          </div>
        </div>

        <h3 className="font-default font-bold text-2xl lg:text-3xl leading-tight tracking-tight text-foreground mb-4 group-hover:text-secondary transition-colors duration-300">
          <a href={watchUrl} target="_blank" rel="noopener noreferrer">{log.title}</a>
        </h3>

        <p className="font-serif text-[15px] leading-relaxed text-muted-foreground mb-6">
          {log.description}
        </p>

        <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-foreground/80 hover:text-foreground transition-colors duration-300 w-fit">
          <span className="border-b border-transparent hover:border-current pb-1">
            WATCH ON YOUTUBE
          </span>
          <ArrowUpRightIcon className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </article>;
}
function ResearchRecords() {
  return <section data-section-id="2297" id="mission-logs" className="bg-background text-foreground py-24 md:py-32 lg:py-44 border-t border-border relative overflow-hidden">
      {/* ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-secondary/5 blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-accent/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative">
        {/* Header */}
        <div className="relative mb-20 lg:mb-28">
          {/* ghost watermark */}
          <span aria-hidden="true" className="pointer-events-none absolute -top-16 right-0 select-none font-mono font-light text-[14rem] md:text-[18rem] lg:text-[20vw] leading-none" style={{
          WebkitTextStroke: "1px var(--border)",
          color: "transparent"
        }}>
            ✦
          </span>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-secondary" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-secondary">
              ASTROMOTO — FIELD DISPATCHES
            </span>
          </div>

          <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground mb-8">
            MISSION
            <br />
            LOGS
          </h2>

          <p className="font-serif text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Long-form field records from the AstroMoto project — hauling a full deep-sky rig
            into the backcountry by motorcycle and bringing the data back. Each entry documents
            a single expedition: the route, the setup, what failed, and what the sky gave up.
          </p>

          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            005 TRANSMISSIONS / 2025 SEASON
          </p>
        </div>

        {/* Log list */}
        <div className="flex flex-col">
          {missionLogs.map((log, i) => <LogEntry key={log.id} log={log} index={i} />)}
        </div>

        {/* Footer CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-20 pt-12 border-t border-border">
          <p className="font-serif text-base text-muted-foreground">
            New dispatches as the season allows. Subscribe on YouTube to follow the project.
          </p>
          <a href="https://www.youtube.com/@SolObservatory" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-foreground text-background hover:bg-secondary hover:text-secondary-foreground font-mono text-xs uppercase tracking-widest h-12 px-6 rounded-md shadow-xl transition-all">
            VISIT THE CHANNEL
            <ArrowUpRightIcon className="size-4" />
          </a>
        </div>
      </div>
    </section>;
}
export default ResearchRecords;
export { ResearchRecords };